import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getClientById } from '../utils/apiService';
import * as predefinedValues from '../data/predefinedValues';
import { serverTypes } from '../utils/serverModel';
import { checkCriticalVersion } from '../data/predefinedValues';
import { lightenColor } from '../utils/colorUtils';
import ServerIcon from '../components/ServerIcon';
import VersionHistoryCompact from '../components/VersionHistoryCompact';
import UpdateReport from '../components/UpdateReport';
import TeleMaintenanceButton from '../components/TeleMaintenanceButton';
import TeleMaintenanceHistory from '../components/TeleMaintenanceHistory';
import BastionButton from '../components/BastionButton'; // NOUVEAU : Bouton bastion
import PuttyButtonUltimate from '../components/PuttyButtonUltimate'; // Version ONE-CLICK ultimate ! 🚀
import ConnectionProcedureViewer from '../components/ConnectionProcedureViewer'; // NOUVEAU : Viewer procédure

const ClientDetails = () => {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedModifications, setExpandedModifications] = useState({});
  const [expandedUpdatesSection, setExpandedUpdatesSection] = useState(false);
  const [expandedUpdates, setExpandedUpdates] = useState({});
  const [serversWithCriticalVersions, setServersWithCriticalVersions] = useState([]);
  const [sslInfo, setSslInfo] = useState({
    patientPortal: null,
    carePortal: null,
    loading: false
  });
  const [showUpdateReport, setShowUpdateReport] = useState(false);
  const [selectedUpdateForReport, setSelectedUpdateForReport] = useState(null);

  useEffect(() => {
    // Fonction pour charger un client depuis l'API
    const loadClient = async () => {
      const data = await getClientById(id);
      
      if (data) {
        // Ne pas utiliser prepareClientForDisplay pour éviter de convertir des valeurs personnalisées en 'custom'
        setClient(data);
      } else {
        setClient(null);
      }
      
      // Vérifier les versions critiques sur tous les serveurs
      if (data && data.servers) {
        const criticalServers = [];
        
        data.servers.forEach((server, index) => {
          const criticalComponents = [];
          
          // Vérifier MariaDB
          const mariadbAlert = checkCriticalVersion('mariadb', server.mariadbVersion);
          if (mariadbAlert) {
            criticalComponents.push({
              component: 'MariaDB',
              version: server.mariadbVersion,
              ...mariadbAlert
            });
          }
          
          // Vérifier NodeJS
          const nodejsAlert = checkCriticalVersion('nodejs', server.nodejsVersion);
          if (nodejsAlert) {
            criticalComponents.push({
              component: 'NodeJS',
              version: server.nodejsVersion,
              ...nodejsAlert
            });
          }
          
          // Vérifier Apache
          const apacheAlert = checkCriticalVersion('apache', server.apacheVersion);
          if (apacheAlert) {
            criticalComponents.push({
              component: 'Apache',
              version: server.apacheVersion,
              ...apacheAlert
            });
          }
          
          // Vérifier les applications
          const patientAppAlert = checkCriticalVersion('gitPatientApp', server.gitPatientAppVersion);
          if (patientAppAlert) {
            criticalComponents.push({
              component: 'Patient App',
              version: server.gitPatientAppVersion,
              ...patientAppAlert
            });
          }
          
          const managerAppAlert = checkCriticalVersion('gitManagerFrontApp', server.gitManagerFrontAppVersion);
          if (managerAppAlert) {
            criticalComponents.push({
              component: 'Manager Front App',
              version: server.gitManagerFrontAppVersion,
              ...managerAppAlert
            });
          }
          
          const marshallerAlert = checkCriticalVersion('gitMarshallerVersion', server.gitMarshallerVersion);
          if (marshallerAlert) {
            criticalComponents.push({
              component: 'Marshaller',
              version: server.gitMarshallerVersion,
              ...marshallerAlert
            });
          }
          
          const marshallerInteropAlert = checkCriticalVersion('gitMarshallerInteropVersion', server.gitMarshallerInteropVersion);
          if (marshallerInteropAlert) {
            criticalComponents.push({
              component: 'Marshaller Interop',
              version: server.gitMarshallerInteropVersion,
              ...marshallerInteropAlert
            });
          }
          
          if (criticalComponents.length > 0) {
            criticalServers.push({
              serverIndex: index,
              serverName: server.name,
              serverType: server.type,
              components: criticalComponents
            });
          }
        });
        
        setServersWithCriticalVersions(criticalServers);
      }
      
      setLoading(false);
    };
    
    loadClient();
  }, [id]);

  const checkSSL = async (portalType) => {
    const url = portalType === 'patient' ? client.patientPortalUrl : client.carePortalUrl;
    
    if (!url) {
      alert(`URL du portail ${portalType === 'patient' ? 'patient' : 'soignant'} non définie.`);
      return;
    }
    
    setSslInfo(prev => ({
      ...prev,
      loading: true
    }));
    
    try {
      const response = await fetch(`/api/check-ssl?url=${encodeURIComponent(url)}`);
      const data = await response.json();
      
      if (response.ok) {
        setSslInfo(prev => ({
          ...prev,
          [portalType === 'patient' ? 'patientPortal' : 'carePortal']: data,
          loading: false
        }));
      } else {
        alert(`Erreur: ${data.error}`);
        setSslInfo(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      alert(`Erreur lors de la vérification du certificat: ${error.message}`);
      setSslInfo(prev => ({ ...prev, loading: false }));
    }
  };
  
  const getSSLStatusColor = (status) => {
    switch(status) {
      case 'expired': return 'bg-red-100 text-red-800 border-red-300';
      case 'warning': return 'bg-amber-100 text-amber-800 border-amber-300';
      case 'valid': return 'bg-green-100 text-green-800 border-green-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleModifications = (serverIndex, component) => {
    setExpandedModifications(prev => {
      const key = `${serverIndex}-${component}`;
      return {
        ...prev,
        [key]: !prev[key]
      };
    });
  };
  
  const toggleUpdateExpand = (index) => {
    setExpandedUpdates(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };
  
  const getStatusClass = (status) => {
    switch(status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'partial':
        return 'bg-amber-100 text-amber-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  const getStatusLabel = (status) => {
    switch(status) {
      case 'completed':
        return 'Complétée';
      case 'partial':
        return 'Partielle';
      case 'cancelled':
        return 'Annulée';
      default:
        return 'En cours';
    }
  };

  const isModificationExpanded = (serverIndex, component) => {
    const key = `${serverIndex}-${component}`;
    return expandedModifications[key] || false;
  };

  const getModificationCountBadge = (server, component) => {
    if (!server.localModifications || !server.localModifications[component]) return null;
    
    const count = server.localModifications[component].length;
    if (count === 0) return null;
    
    return (
      <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-medium rounded-full">
        {count}
      </span>
    );
  };

  const openUpdateReport = (update) => {
    setSelectedUpdateForReport(update);
    setShowUpdateReport(true);
  };
  
  const closeUpdateReport = () => {
    setShowUpdateReport(false);
    setSelectedUpdateForReport(null);
  };

  if (loading) {
    return <div className="text-center py-10">Chargement...</div>;
  }

  if (!client) {
    return (
      <div className="text-center py-10">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 max-w-2xl mx-auto">
          <svg className="mx-auto h-12 w-12 text-red-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h2 className="text-2xl font-bold mb-2 text-red-800">Client non trouvé</h2>
          <p className="text-gray-600 mb-2">Le client avec l'ID #{id} n'existe pas ou a été supprimé.</p>
          <p className="text-sm text-gray-500 mb-6">
            Si vous venez de créer ce client et que vous voyez ce message, il se peut qu'il y ait un problème avec l'ID du client.
            Essayez de retourner à la liste et de rechercher le client par son nom.
          </p>
          <div className="space-y-2">
            <Link to="/" className="block bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
              Retour à la liste des clients
            </Link>
            <Link to="/add-client" className="block text-blue-600 hover:underline">
              Créer un nouveau client
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
      <h1 className="text-3xl font-bold">{client.name}</h1>
      <div className="flex space-x-2">
      <Link
      to={`/edit-client/${client.id}`}
      className="hoppen-btn px-4 py-2 rounded shadow hover:shadow-md transition-all"
      >
      Modifier
      </Link>
      <BastionButton client={client} />
      <TeleMaintenanceButton client={client} />
      <Link
      to={`/client/${client.id}/engineering`}
      className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded shadow hover:shadow-md transition-all"
      >
      📁 Dossier Ingénierie
      </Link>
      <Link
      to={`/client/${client.id}/add-update`}
      className="hoppen-btn px-4 py-2 rounded shadow hover:shadow-md transition-all"
      >
      + Nouvelle mise à jour
      </Link>
      </div>
      </div>

      {/* Affichage des alertes pour les versions critiques */}
      {serversWithCriticalVersions.length > 0 && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h2 className="text-lg font-semibold text-amber-800 mb-2">
            ⚠️ Alertes de versions obsolètes ({serversWithCriticalVersions.length} serveur{serversWithCriticalVersions.length > 1 ? 's' : ''} affecté{serversWithCriticalVersions.length > 1 ? 's' : ''})
          </h2>
          
          <div className="space-y-3">
            {serversWithCriticalVersions.map((serverInfo, idx) => (
              <div key={idx} className="bg-white border border-amber-100 rounded p-3">
                <div className="flex items-center mb-2">
                  <h3 className="font-medium" style={{ color: client.servers[serverInfo.serverIndex].color }}>
                    {serverInfo.serverName || `Serveur ${serverInfo.serverIndex + 1}`}
                  </h3>
                  <span 
                    className="ml-2 text-xs px-2 py-0.5 rounded font-medium" 
                    style={{ backgroundColor: client.servers[serverInfo.serverIndex].color, color: 'white' }}
                  >
                    {serverInfo.serverType || 'Serveur tout-en-un'}
                  </span>
                </div>
                
                <ul className="divide-y divide-gray-100">
                  {serverInfo.components.map((component, compIdx) => (
                    <li key={compIdx} className="py-2">
                      <div className="flex items-start">
                        <div className={`mt-0.5 mr-2 h-4 w-4 flex-shrink-0 rounded-full ${component.level === 'critical' ? 'bg-red-500' : component.level === 'warning' ? 'bg-amber-500' : 'bg-blue-500'}`}></div>
                        <div>
                          <p className="font-medium">
                            {component.component}: <span className="font-mono">{component.version}</span>
                          </p>
                          <p className="text-sm text-gray-600">{component.message}</p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
                
                <div className="mt-2 text-right">
                  <a 
                    href={`#server-${serverInfo.serverIndex}`} 
                    className="text-sm text-blue-600 hover:underline"
                  >
                    Aller au serveur
                  </a>
                </div>
              </div>
            ))}
          </div>
          
          <p className="mt-3 text-sm text-amber-700">
            Une mise à jour est recommandée pour ces composants afin d'assurer la sécurité et les fonctionnalités optimales.
          </p>
        </div>
      )}

      {/* Historique des demandes de télémaintenance */}
      <TeleMaintenanceHistory client={client} />

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-6">
          <h2 className="text-xl font-semibold mb-4">Informations générales</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <p className="text-gray-600">Description:</p>
              <p>{client.description || 'Aucune description'}</p>
            </div>
            <div>
              <p className="text-gray-600">Environnement:</p>
              <p>{client.environment || 'Non spécifié'}</p>
            </div>
            <div className="col-span-full">
              <p className="text-gray-600">Télémaintenance:</p>
              <p className={client.remoteMaintenance ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                {client.remoteMaintenance 
                  ? '✅ À débloquer pour accès aux serveurs' 
                  : '❌ Accès non nécessaire'}
              </p>
            </div>
          </div>
          
          {/* Affichage des URLs des portails */}
          {(client.patientPortalUrl || client.carePortalUrl) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {client.patientPortalUrl && (
                <div>
                  <p className="text-gray-600">URL du portail patient:</p>
                  <div className="flex items-center">
                    <a 
                      href={client.patientPortalUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline mr-2"
                    >
                      {client.patientPortalUrl}
                    </a>
                    <button 
                      type="button" 
                      className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded text-xs"
                      onClick={() => checkSSL('patient')}
                      disabled={sslInfo.loading}
                    >
                      {sslInfo.loading ? 'Vérification...' : 'Vérifier SSL'}
                    </button>
                  </div>
                  <div className="flex items-center mt-1">
                    <span className={`text-sm ${client.patientPortalUrlExternallyAccessible !== false ? 'text-green-600' : 'text-orange-600'}`}>
                      {client.patientPortalUrlExternallyAccessible !== false ? '✓ Accessible depuis l\'extérieur' : '⚠ Accès interne uniquement'}
                    </span>
                  </div>
                  {sslInfo.patientPortal && (
                    <div className={`mt-2 p-3 border rounded ${getSSLStatusColor(sslInfo.patientPortal.status)}`}>
                      <div className="flex items-center mb-1">
                        <div className={`h-3 w-3 rounded-full mr-2 ${sslInfo.patientPortal.status === 'valid' ? 'bg-green-500' : 
                          sslInfo.patientPortal.status === 'warning' ? 'bg-amber-500' : 'bg-red-500'}`}></div>
                        <span className="font-medium text-sm">
                          {sslInfo.patientPortal.status === 'valid' ? 'Certificat valide' : 
                            sslInfo.patientPortal.status === 'warning' ? 'Certificat à renouveler bientôt' : 
                            'Certificat expiré'}
                        </span>
                      </div>
                      <p className="text-xs mb-1">
                        <span className="font-medium">Valide jusqu'au:</span> {formatDate(sslInfo.patientPortal.validTo)}
                      </p>
                      <p className="text-xs">
                        <span className="font-medium">Jours restants:</span> {sslInfo.patientPortal.daysRemaining}
                      </p>
                    </div>
                  )}
                </div>
              )}
              {client.carePortalUrl && (
                <div>
                  <p className="text-gray-600">URL du portail soignant:</p>
                  <div className="flex items-center">
                    <a 
                      href={client.carePortalUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline mr-2"
                    >
                      {client.carePortalUrl}
                    </a>
                    <button 
                      type="button" 
                      className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded text-xs"
                      onClick={() => checkSSL('care')}
                      disabled={sslInfo.loading}
                    >
                      {sslInfo.loading ? 'Vérification...' : 'Vérifier SSL'}
                    </button>
                  </div>
                  <div className="flex items-center mt-1">
                    <span className={`text-sm ${client.carePortalUrlExternallyAccessible !== false ? 'text-green-600' : 'text-orange-600'}`}>
                      {client.carePortalUrlExternallyAccessible !== false ? '✓ Accessible depuis l\'extérieur' : '⚠ Accès interne uniquement'}
                    </span>
                  </div>
                  {sslInfo.carePortal && (
                    <div className={`mt-2 p-3 border rounded ${getSSLStatusColor(sslInfo.carePortal.status)}`}>
                      <div className="flex items-center mb-1">
                        <div className={`h-3 w-3 rounded-full mr-2 ${sslInfo.carePortal.status === 'valid' ? 'bg-green-500' : 
                          sslInfo.carePortal.status === 'warning' ? 'bg-amber-500' : 'bg-red-500'}`}></div>
                        <span className="font-medium text-sm">
                          {sslInfo.carePortal.status === 'valid' ? 'Certificat valide' : 
                            sslInfo.carePortal.status === 'warning' ? 'Certificat à renouveler bientôt' : 
                            'Certificat expiré'}
                        </span>
                      </div>
                      <p className="text-xs mb-1">
                        <span className="font-medium">Valide jusqu'au:</span> {formatDate(sslInfo.carePortal.validTo)}
                      </p>
                      <p className="text-xs">
                        <span className="font-medium">Jours restants:</span> {sslInfo.carePortal.daysRemaining}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* NOUVEAU : Affichage avancé de la procédure de connexion */}
          <div className="mb-6">
            <ConnectionProcedureViewer procedure={client.connectionProcedure} />
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3">Check général de l'installation</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-gray-600">PROD:</p>
                <p>{client.checkGeneral?.prod || 'Non spécifié'}</p>
              </div>
              <div>
                <p className="text-gray-600">QUAL:</p>
                <p>{client.checkGeneral?.qual || 'Non spécifié'}</p>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <div className="version-history bg-white rounded-lg shadow-md overflow-hidden">
              <div 
                className="version-history-header p-3 flex items-center cursor-pointer hover:opacity-90"
                onClick={() => setExpandedUpdatesSection(!expandedUpdatesSection)}
              >
                <div className="version-history-icon mr-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
                <h3 className="text-lg font-semibold">Mise à jour</h3>
                {client.updates && client.updates.length > 0 && (
                  <span className="ml-2 bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">
                    {client.updates.length}
                  </span>
                )}
                <div className="ml-auto flex items-center">
                  <Link
                    to={`/client/${client.id}/add-update`}
                    className="hoppen-btn px-3 py-1 rounded text-sm mr-3 shadow"
                    onClick={(e) => e.stopPropagation()}
                  >
                    + Ajouter
                  </Link>
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                    className={`transition-transform duration-300 ${expandedUpdatesSection ? 'transform rotate-180' : ''}`}
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </div>
              </div>
              
              <div className={`transition-all duration-300 overflow-hidden ${expandedUpdatesSection ? 'max-h-full opacity-100' : 'max-h-0 opacity-0'}`}>
                {client.updates && client.updates.length > 0 ? (
                  <div className="p-3 space-y-2">
                    {client.updates.slice().reverse().map((update, index) => (
                      <div 
                        key={update.id || index} 
                        className={`border rounded-lg overflow-hidden shadow-sm ${update.updateType === 'Production' ? 'border-purple-200' : 'border-blue-200'}`}
                      >
                        <div 
                          className={`flex flex-wrap justify-between items-center p-3 cursor-pointer ${update.updateType === 'Production' ? 'bg-purple-50 border-purple-300' : 'bg-blue-50 border-blue-300'}`}
                          onClick={() => toggleUpdateExpand(index)}
                        >
                          <div className="flex items-center space-x-2">
                            <h4 className="font-semibold">{update.title}</h4>
                            {update.updateType && (
                              <span className={`${update.updateType === 'Production' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'} text-xs px-2 py-0.5 rounded-full font-medium`}>
                                {update.updateType}
                              </span>
                            )}
                            {update.status && (
                              <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusClass(update.status)}`}>
                                {getStatusLabel(update.status)}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center">
                            <span className="text-sm text-gray-500 mr-2">{new Date(update.date).toLocaleDateString()}</span>
                            <svg 
                              xmlns="http://www.w3.org/2000/svg" 
                              width="14" 
                              height="14" 
                              viewBox="0 0 24 24" 
                              fill="none" 
                              stroke="currentColor" 
                              strokeWidth="2" 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                              className={`transition-transform duration-300 ${expandedUpdates[index] ? 'transform rotate-180' : ''}`}
                            >
                              <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>
                          </div>
                        </div>
                        
                        {expandedUpdates[index] && (
                          <div className="p-3 bg-white">
                            {update.description && (
                              <p className="text-gray-700 mb-3">{update.description}</p>
                            )}
                            
                            {update.ticketUrl && (
                              <div className="mb-3">
                                <a 
                                  href={update.ticketUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-sm text-blue-600 hover:underline flex items-center"
                                >
                                  <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                  </svg>
                                  Ticket: {update.ticketUrl.includes('atlassian.net/browse/') 
                                    ? update.ticketUrl.split('atlassian.net/browse/')[1] 
                                    : update.ticketUrl}
                                </a>
                              </div>
                            )}
                            
                            {(update.status === 'cancelled' || update.status === 'partial') && update.statusReason && (
                              <div className={`mb-3 p-2 rounded ${update.status === 'cancelled' ? 'bg-red-50 border border-red-100' : 'bg-yellow-50 border border-yellow-100'}`}>
                                <p className="text-sm">
                                  <strong>{update.status === 'cancelled' ? "Raison de l'annulation :" : "Détails de la réalisation partielle :"}</strong> {update.statusReason}
                                </p>
                              </div>
                            )}
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                              {/* Colonne MISE À JOUR */}
                              <div className="border p-2 rounded bg-gray-50">
                                <h5 className="font-semibold text-sm text-gray-600 mb-2 border-b pb-1">MISE À JOUR</h5>
                                <ul className="text-xs space-y-1">
                                  {update.update.dbSchemaUpdated && <li className="text-green-600">✓ MAJ du schéma de base de données</li>}
                                  {update.update.servicesRestarted && <li className="text-green-600">✓ Redémarrage des services</li>}
                                  {update.update.mobileAppUpdated && <li className="text-green-600">✓ MAJ Application mobile</li>}
                                  {update.update.buildsUpdated && <li className="text-green-600">✓ Mise à jour des builds</li>}
                                  {update.update.mobileAppCreated && <li className="text-green-600">✓ Création application mobile</li>}
                                  {update.update.internalTestsCompleted && <li className="text-green-600">✓ Tests internes réalisés</li>}
                                  {update.update.appDeployedToStores && <li className="text-green-600">✓ Mise en téléchargement sur les stores</li>}
                                </ul>
                              </div>
                              
                              {/* Colonne GENERAL */}
                              <div className="border p-2 rounded bg-gray-50">
                                <h5 className="font-semibold text-sm text-gray-600 mb-2 border-b pb-1">GENERAL</h5>
                                <div className="mb-1">
                                  <h6 className="text-xs font-medium text-gray-500">Production</h6>
                                  <ul className="text-xs space-y-0.5">
                                    {update.general.production.secureClientConnection && 
                                      <li className="text-green-600">✓ Connexion sécurisée</li>}
                                    {update.general.production.softwareComponentsRecovery && 
                                      <li className="text-green-600">✓ Récup. briques logicielles</li>}
                                    {update.general.production.softwareDependenciesRecovery && 
                                      <li className="text-green-600">✓ Récup. dépendances</li>}
                                  </ul>
                                </div>
                                
                                <div>
                                  <h6 className="text-xs font-medium text-gray-500">Vérification</h6>
                                  <ul className="text-xs space-y-0.5">
                                    {update.general.verification.carePortalDisplay && 
                                      <li className="text-green-600">✓ Affichage portail soignant</li>}
                                    {update.general.verification.carePortalLogin && 
                                      <li className="text-green-600">✓ Connexion portail soignant</li>}
                                    {update.general.verification.patientSearch && 
                                      <li className="text-green-600">✓ Recherche patient</li>}
                                    {update.general.verification.pairingSheetGeneration && 
                                      <li className="text-green-600">✓ Feuille d'appairage</li>}
                                    {update.general.verification.patientPortalDisplay && 
                                      <li className="text-green-600">✓ Affichage portail patient</li>}
                                    {update.general.verification.patientPortalLogin && 
                                      <li className="text-green-600">✓ Connexion portail patient</li>}
                                  </ul>
                                </div>
                              </div>
                              
                              {/* Colonne COMMITS */}
                              <div className="border p-2 rounded bg-gray-50">
                                <h5 className="font-semibold text-sm text-gray-600 mb-2 border-b pb-1">COMMITS</h5>
                                <ul className="text-xs space-y-1 font-mono">
                                  {update.commits.manager && <li><span className="font-medium text-gray-500">MANAGER:</span> {update.commits.manager}</li>}
                                  {update.commits.marshallerFrontal && <li><span className="font-medium text-gray-500">M-FRONTAL:</span> {update.commits.marshallerFrontal}</li>}
                                  {update.commits.marshallerInterop && <li><span className="font-medium text-gray-500">M-INTEROP:</span> {update.commits.marshallerInterop}</li>}
                                  {update.commits.patientApp && <li><span className="font-medium text-gray-500">PATIENT:</span> {update.commits.patientApp}</li>}
                                  {update.commits.datamodel && <li><span className="font-medium text-gray-500">DATAMODEL:</span> {update.commits.datamodel}</li>}
                                </ul>
                              </div>
                            </div>
                            
                            {update.additionalNotes && (
                              <div className="mt-3 pt-2 border-t">
                                <p className="text-xs text-gray-600">{update.additionalNotes}</p>
                              </div>
                            )}
                            
                            <div className="mt-3 pt-2 border-t flex justify-end">
                              <button
                                onClick={() => openUpdateReport(update)}
                                className="bg-purple-500 hover:bg-purple-600 text-white px-3 py-1 rounded text-sm flex items-center"
                              >
                                <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                                </svg>
                                Exporter le compte-rendu
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 bg-gray-50 p-4 text-center">
                    Aucune mise à jour enregistrée. 
                    <Link to={`/client/${client.id}/add-update`} className="text-purple-600 hover:underline ml-1">
                      Ajouter une mise à jour
                    </Link>
                  </p>
                )}
              </div>
            </div>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold mb-3">Serveurs ({client.servers?.length || 0})</h3>
            
            {client.servers && client.servers.length > 0 ? (
              <div className="space-y-4">
                {client.servers.map((server, serverIndex) => (
                  <div key={serverIndex} className="p-4 rounded border" style={{ backgroundColor: `${server.color ? `${server.color}15` : '#f9fafb'}`, borderColor: server.color || '#e5e7eb' }} id={`server-${serverIndex}`}>
                    <div className="flex items-center justify-between mb-4 border-b pb-2">
                      <div className="flex items-center">
                        <h4 className="font-semibold" style={{ color: server.color }}>{server.name || `Serveur ${serverIndex + 1}`}</h4>
                        <span className="ml-2 server-badge" style={{ background: server.gradient || `linear-gradient(135deg, ${server.color || '#3B82F6'}, ${lightenColor(server.color || '#3B82F6', 40)})` }}>
                          <ServerIcon type={server.type || 'Serveur tout-en-un'} />
                          <span>{server.type || 'Serveur tout-en-un'}</span>
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <PuttyButtonUltimate server={server} client={client} />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                      <div>
                        <p className="text-gray-600">Type de serveur:</p>
                        <p className="font-medium" style={{ color: server.color || '#3B82F6' }}>{server.type || 'Serveur tout-en-un'}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">OS:</p>
                        <p>{server.os || 'Non spécifié'}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">RAM:</p>
                        <p>{server.ram || 'Non spécifié'}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">CPU:</p>
                        <p>{server.cpu || 'Non spécifié'}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Espace disque:</p>
                        <p>{server.diskSpace || 'Non spécifié'}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Dossier d'installation APP:</p>
                        <p>{server.appInstallFolder || 'Non spécifié'}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Version MariaDB:</p>
                        <p>{server.mariadbVersion || 'Non spécifié'}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Dossier d'installation BDD:</p>
                        <p>{server.dbInstallFolder || 'Non spécifié'}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">BDD optimisée:</p>
                        <p>{server.dbOptimized ? 'Oui' : 'Non'}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Version Apache:</p>
                        <p>{server.apacheVersion || 'Non spécifié'}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Version NodeJS:</p>
                        <p>{server.nodejsVersion || 'Non spécifié'}</p>
                      </div>
                    </div>
                    
                    {/* Section des versions Git avec modifications locales */}
                    <div className="border-t border-gray-200 pt-4 mt-4">
                      <h5 className="font-medium mb-3">Versions Git & Modifications locales</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <button 
                            onClick={() => toggleModifications(serverIndex, 'patientApp')}
                            className="flex items-center font-medium text-left w-full mb-1"
                          >
                            <span className="text-gray-600">Version Git patient-app:</span>
                            {getModificationCountBadge(server, 'patientApp')}
                            {server.localModifications?.patientApp?.length > 0 && (
                              <svg className={`ml-1 w-4 h-4 transition-transform ${isModificationExpanded(serverIndex, 'patientApp') ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                              </svg>
                            )}
                          </button>
                          <p className="mb-2">{server.gitPatientAppVersion || 'Non spécifié'}</p>
                          
                          {/* Affichage des modifications locales pour patient-app */}
                          {isModificationExpanded(serverIndex, 'patientApp') && server.localModifications?.patientApp?.length > 0 && (
                            <div className="pl-4 mb-4 border-l-2 border-amber-300">
                              {server.localModifications.patientApp.map((mod, modIndex) => (
                                <div key={modIndex} className="mb-3">
                                  <p className="font-medium text-sm">{mod.filename || 'Sans nom'}</p>
                                  <p className="text-sm text-gray-600 mb-1">{mod.description || 'Aucune description'}</p>
                                  {mod.code && (
                                    <pre className="bg-gray-800 text-white p-2 rounded text-xs overflow-x-auto whitespace-pre-wrap">
                                      {mod.code}
                                    </pre>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        <div>
                          <button 
                            onClick={() => toggleModifications(serverIndex, 'managerFrontApp')}
                            className="flex items-center font-medium text-left w-full mb-1"
                          >
                            <span className="text-gray-600">Version Git manager-front-app:</span>
                            {getModificationCountBadge(server, 'managerFrontApp')}
                            {server.localModifications?.managerFrontApp?.length > 0 && (
                              <svg className={`ml-1 w-4 h-4 transition-transform ${isModificationExpanded(serverIndex, 'managerFrontApp') ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                              </svg>
                            )}
                          </button>
                          <p className="mb-2">{server.gitManagerFrontAppVersion || 'Non spécifié'}</p>
                          
                          {/* Affichage des modifications locales pour manager-front-app */}
                          {isModificationExpanded(serverIndex, 'managerFrontApp') && server.localModifications?.managerFrontApp?.length > 0 && (
                            <div className="pl-4 mb-4 border-l-2 border-amber-300">
                              {server.localModifications.managerFrontApp.map((mod, modIndex) => (
                                <div key={modIndex} className="mb-3">
                                  <p className="font-medium text-sm">{mod.filename || 'Sans nom'}</p>
                                  <p className="text-sm text-gray-600 mb-1">{mod.description || 'Aucune description'}</p>
                                  {mod.code && (
                                    <pre className="bg-gray-800 text-white p-2 rounded text-xs overflow-x-auto whitespace-pre-wrap">
                                      {mod.code}
                                    </pre>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        <div>
                          <button 
                            onClick={() => toggleModifications(serverIndex, 'marshaller')}
                            className="flex items-center font-medium text-left w-full mb-1"
                          >
                            <span className="text-gray-600">Version Git marshaller:</span>
                            {getModificationCountBadge(server, 'marshaller')}
                            {server.localModifications?.marshaller?.length > 0 && (
                              <svg className={`ml-1 w-4 h-4 transition-transform ${isModificationExpanded(serverIndex, 'marshaller') ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                              </svg>
                            )}
                          </button>
                          <p className="mb-2">{server.gitMarshallerVersion || 'Non spécifié'}</p>
                          
                          {/* Affichage des modifications locales pour marshaller */}
                          {isModificationExpanded(serverIndex, 'marshaller') && server.localModifications?.marshaller?.length > 0 && (
                            <div className="pl-4 mb-4 border-l-2 border-amber-300">
                              {server.localModifications.marshaller.map((mod, modIndex) => (
                                <div key={modIndex} className="mb-3">
                                  <p className="font-medium text-sm">{mod.filename || 'Sans nom'}</p>
                                  <p className="text-sm text-gray-600 mb-1">{mod.description || 'Aucune description'}</p>
                                  {mod.code && (
                                    <pre className="bg-gray-800 text-white p-2 rounded text-xs overflow-x-auto whitespace-pre-wrap">
                                      {mod.code}
                                    </pre>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        <div>
                          <button 
                            onClick={() => toggleModifications(serverIndex, 'marshallerInterop')}
                            className="flex items-center font-medium text-left w-full mb-1"
                          >
                            <span className="text-gray-600">Version Git marshaller-interop:</span>
                            {getModificationCountBadge(server, 'marshallerInterop')}
                            {server.localModifications?.marshallerInterop?.length > 0 && (
                              <svg className={`ml-1 w-4 h-4 transition-transform ${isModificationExpanded(serverIndex, 'marshallerInterop') ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                              </svg>
                            )}
                          </button>
                          <p className="mb-2">{server.gitMarshallerInteropVersion || 'Non spécifié'}</p>
                          
                          {/* Affichage des modifications locales pour marshaller-interop */}
                          {isModificationExpanded(serverIndex, 'marshallerInterop') && server.localModifications?.marshallerInterop?.length > 0 && (
                            <div className="pl-4 mb-4 border-l-2 border-amber-300">
                              {server.localModifications.marshallerInterop.map((mod, modIndex) => (
                                <div key={modIndex} className="mb-3">
                                  <p className="font-medium text-sm">{mod.filename || 'Sans nom'}</p>
                                  <p className="text-sm text-gray-600 mb-1">{mod.description || 'Aucune description'}</p>
                                  {mod.code && (
                                    <pre className="bg-gray-800 text-white p-2 rounded text-xs overflow-x-auto whitespace-pre-wrap">
                                      {mod.code}
                                    </pre>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="border-t border-gray-200 pt-4 mt-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-gray-600">BDD optimisée:</p>
                          <p className={server.dbOptimized === undefined ? 'text-gray-500' : (server.dbOptimized ? 'text-green-600 font-medium' : 'text-orange-600 font-medium')}>
                            {server.dbOptimized === undefined ? 'Non spécifié' : (server.dbOptimized ? '✅ Oui' : '⚠️ Non')}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-600">Sonde déployée:</p>
                          <p className={server.probeDeployed === undefined ? 'text-gray-500' : (server.probeDeployed ? 'text-green-600 font-medium' : 'text-orange-600 font-medium')}>
                            {server.probeDeployed === undefined ? 'Non spécifié' : (server.probeDeployed ? '✅ Oui' : '⚠️ Non')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">Aucun serveur enregistré</p>
            )}
          </div>
          
          {/* Ajout du composant d'historique des versions */}
          <VersionHistoryCompact client={client} />
        </div>
      </div>
      
      {/* Modal pour l'export du compte-rendu */}
      {showUpdateReport && selectedUpdateForReport && (
        <UpdateReport 
          client={client} 
          update={selectedUpdateForReport} 
          onClose={closeUpdateReport}
        />
      )}
    </div>
  );
};

export default ClientDetails;